import { useState, useCallback } from 'react';
import { Upload, FileText, X, CheckCircle, AlertCircle, Loader, AlertTriangle, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BulkUploadModal({ onClose, onComplete }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState({});
  const [paymentStatus, setPaymentStatus] = useState({}); // Track paid/unpaid for each file
  const [isUploading, setIsUploading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    
    // Initialize status and payment status for each file
    const status = {};
    const payment = {};
    files.forEach(file => {
      status[file.name] = { status: 'pending', progress: 0 };
      payment[file.name] = 'unpaid'; // Default to unpaid
    });
    setUploadStatus(status);
    setPaymentStatus(payment);
  }, []);

  const removeFile = (fileName) => {
    setSelectedFiles(files => files.filter(f => f.name !== fileName));
    setUploadStatus(status => {
      const newStatus = { ...status };
      delete newStatus[fileName];
      return newStatus;
    });
    setPaymentStatus(payment => {
      const newPayment = { ...payment };
      delete newPayment[fileName];
      return newPayment;
    });
  };

  const togglePaymentStatus = (fileName) => {
    setPaymentStatus(prev => ({
      ...prev,
      [fileName]: prev[fileName] === 'paid' ? 'unpaid' : 'paid'
    }));
  };

  const markAllPaid = () => {
    const newPayment = {};
    selectedFiles.forEach(file => {
      newPayment[file.name] = 'paid';
    });
    setPaymentStatus(newPayment);
  };

  const markAllUnpaid = () => {
    const newPayment = {};
    selectedFiles.forEach(file => {
      newPayment[file.name] = 'unpaid';
    });
    setPaymentStatus(newPayment);
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    setIsUploading(true);
    let successCount = 0;
    let failCount = 0;
    let duplicateCount = 0;
    let extractionFailedCount = 0;

    // Upload files one by one
    for (const file of selectedFiles) {
      try {
        // Update status to uploading
        setUploadStatus(prev => ({
          ...prev,
          [file.name]: { status: 'uploading', progress: 50 }
        }));

        const formData = new FormData();
        formData.append('file', file);

        // Include payment status in the upload
        const filePaymentStatus = paymentStatus[file.name] || 'unpaid';
        const response = await axios.post(`${API}/invoices/upload?payment_status=${filePaymentStatus}`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Check if extraction failed
        const extractedData = response.data.extracted_data;
        const extractionFailed = response.data.extraction_failed || 
          (!extractedData?.invoice_number && !extractedData?.vendor_name && 
           !extractedData?.total_amount && !extractedData?.invoice_date);

        if (extractionFailed) {
          setUploadStatus(prev => ({
            ...prev,
            [file.name]: { 
              status: 'extraction_failed', 
              progress: 100,
              extractionWarning: 'Failed to extract data - please re-upload with clearer image'
            }
          }));
          successCount++;
          extractionFailedCount++;
          continue;
        }

        // Check for duplicates after upload
        let isDuplicate = false;

        if (extractedData) {
          const duplicateCheckData = {
            invoice_number: extractedData.invoice_number,
            total_amount: extractedData.total_amount,
            vendor_name: extractedData.vendor_name,
            exclude_invoice_id: response.data.invoice_id
          };

          // Only check if we have enough data
          const hasEnoughData = [
            duplicateCheckData.invoice_number,
            duplicateCheckData.total_amount,
            duplicateCheckData.vendor_name
          ].filter(Boolean).length >= 2;

          if (hasEnoughData) {
            try {
              const dupCheckResponse = await axios.post(`${API}/invoices/check-duplicate`, duplicateCheckData, {
                withCredentials: true
              });
              isDuplicate = dupCheckResponse.data.is_duplicate;
            } catch (e) {
              // Ignore duplicate check errors
            }
          }
        }

        // Update status to success (with duplicate warning if applicable)
        setUploadStatus(prev => ({
          ...prev,
          [file.name]: { 
            status: isDuplicate ? 'duplicate' : 'success', 
            progress: 100,
            duplicateWarning: isDuplicate ? 'Similar invoice exists' : null
          }
        }));
        
        successCount++;
        if (isDuplicate) duplicateCount++;
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        
        // Update status to error
        setUploadStatus(prev => ({
          ...prev,
          [file.name]: { 
            status: 'error', 
            progress: 0,
            error: error.response?.data?.detail || 'Upload failed'
          }
        }));
        failCount++;
      }
    }

    setIsUploading(false);

    // Show summary
    if (successCount > 0 && failCount === 0) {
      let message = `Uploaded ${successCount} invoice${successCount > 1 ? 's' : ''}`;
      const warnings = [];
      if (duplicateCount > 0) {
        warnings.push(`${duplicateCount} duplicate${duplicateCount > 1 ? 's' : ''}`);
      }
      if (extractionFailedCount > 0) {
        warnings.push(`${extractionFailedCount} failed extraction`);
      }
      if (warnings.length > 0) {
        message += ` (${warnings.join(', ')})`;
        toast.warning(message);
      } else {
        toast.success(message + '!');
      }
      setTimeout(() => {
        onComplete();
        onClose();
      }, extractionFailedCount > 0 ? 2500 : 1500);
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(`Uploaded ${successCount} invoice${successCount > 1 ? 's' : ''}, ${failCount} failed`);
    } else {
      toast.error('All uploads failed');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'duplicate':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'extraction_failed':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-rose-500" />;
      case 'uploading':
        return <Loader className="w-5 h-5 text-indigo-700 animate-spin" />;
      default:
        return <FileText className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-emerald-50 border-emerald-200';
      case 'duplicate':
        return 'bg-amber-50 border-amber-200';
      case 'extraction_failed':
        return 'bg-orange-50 border-orange-200';
      case 'error':
        return 'bg-rose-50 border-rose-200';
      case 'uploading':
        return 'bg-indigo-50 border-indigo-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          data-testid="bulk-upload-modal"
        >
          <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-heading font-semibold text-slate-900">Bulk Upload Invoices</h2>
              <p className="text-sm text-slate-500 mt-1">Upload multiple invoices at once</p>
            </div>
            <button
              onClick={onClose}
              disabled={isUploading}
              className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
              data-testid="close-bulk-upload"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            {selectedFiles.length === 0 ? (
              <div>
                <input
                  type="file"
                  id="bulk-file-upload"
                  accept=".pdf,.png,.jpg,.jpeg,.bmp,.tif,.tiff,.gif,.heic,.heif"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label
                  htmlFor="bulk-file-upload"
                  className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50/30 transition-all block"
                >
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Upload className="w-8 h-8 text-indigo-700" />
                    </div>
                    <div>
                      <p className="text-lg font-medium text-slate-900 mb-1">
                        Select multiple invoice files
                      </p>
                      <p className="text-sm text-slate-500">
                        Click to browse or drag and drop
                      </p>
                    </div>
                    <p className="text-xs text-slate-400">
                      PDF, PNG, JPG, BMP, TIF, GIF, HEIC • Max 10MB each
                    </p>
                  </div>
                </label>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">
                    {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                  </p>
                  {!isUploading && (
                    <label
                      htmlFor="bulk-file-upload"
                      className="text-sm text-indigo-700 hover:text-indigo-800 cursor-pointer font-medium"
                    >
                      Add more files
                    </label>
                  )}
                  <input
                    type="file"
                    id="bulk-file-upload"
                    accept=".pdf,.png,.jpg,.jpeg,.bmp,.tif,.tiff,.gif,.heic,.heif"
                    multiple
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files);
                      setSelectedFiles(prev => [...prev, ...newFiles]);
                      const status = {};
                      const payment = {};
                      newFiles.forEach(file => {
                        status[file.name] = { status: 'pending', progress: 0 };
                        payment[file.name] = 'unpaid';
                      });
                      setUploadStatus(prev => ({ ...prev, ...status }));
                      setPaymentStatus(prev => ({ ...prev, ...payment }));
                    }}
                    className="hidden"
                  />
                </div>

                {/* Mark All Buttons */}
                {!isUploading && (
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                    <span className="text-xs text-slate-500">Payment status:</span>
                    <button
                      onClick={markAllPaid}
                      className="px-3 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full hover:bg-emerald-200 transition-colors flex items-center gap-1"
                      data-testid="mark-all-paid"
                    >
                      <Check className="w-3 h-3" />
                      Mark all paid
                    </button>
                    <button
                      onClick={markAllUnpaid}
                      className="px-3 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200 transition-colors"
                      data-testid="mark-all-unpaid"
                    >
                      Mark all unpaid
                    </button>
                  </div>
                )}

                <div className="max-h-96 overflow-y-auto space-y-2">
                  {selectedFiles.map((file) => {
                    const status = uploadStatus[file.name] || { status: 'pending' };
                    const isPaid = paymentStatus[file.name] === 'paid';
                    return (
                      <div
                        key={file.name}
                        className={`border rounded-lg p-3 flex items-center justify-between transition-all ${getStatusColor(status.status)}`}
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          {getStatusIcon(status.status)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            {status.error && (
                              <p className="text-xs text-rose-600 mt-1">{status.error}</p>
                            )}
                            {status.duplicateWarning && (
                              <p className="text-xs text-amber-600 mt-1">{status.duplicateWarning}</p>
                            )}
                            {status.extractionWarning && (
                              <p className="text-xs text-orange-600 mt-1">{status.extractionWarning}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Payment Status Toggle */}
                        {!isUploading && status.status === 'pending' && (
                          <div className="flex items-center gap-2 ml-2">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isPaid}
                                onChange={() => togglePaymentStatus(file.name)}
                                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                data-testid={`paid-toggle-${file.name}`}
                              />
                              <span className={`ml-1.5 text-xs font-medium ${isPaid ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {isPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            </label>
                            <button
                              onClick={() => removeFile(file.name)}
                              className="text-slate-400 hover:text-rose-600 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {selectedFiles.length > 0 && (
            <div className="border-t border-slate-200 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={onClose}
                disabled={isUploading}
                className="px-6 py-2.5 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition-all font-medium rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={uploadFiles}
                disabled={isUploading}
                className="px-6 py-2.5 bg-indigo-700 text-white hover:bg-indigo-800 shadow-sm hover:shadow-md transition-all font-medium rounded-lg flex items-center space-x-2 disabled:opacity-50"
                data-testid="start-bulk-upload"
              >
                {isUploading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span>Upload {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''}</span>
                  </>
                )}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default BulkUploadModal;
